import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit2, 
  CheckCircle2, 
  XCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Paperclip,
  Download,
  Eye,
  X,
  FileSpreadsheet,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/app-utils';
import { createNotification } from '@/lib/audit';
import { cn } from '@/lib/utils';
import FileUpload from '@/components/ui/FileUpload';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import * as XLSX from 'xlsx';

const API_BASE = 'https://new.sbdemolition.online/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const parseApiResponse = async (response) => {
  const text = await response.text();
  if (text.startsWith('<')) throw new Error('Server returned an error. Check server logs.');
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid server response');
  }
};

const accountingAPI = {
  getProjectAccounting: async (projectId) => {
    const response = await fetch(
      `${API_BASE}/accounting/project-accounting-list.php?projectId=${projectId}`,
      { method: 'GET', credentials: 'include', headers: getAuthHeaders() }
    );
    return parseApiResponse(response);
  },

  createRecord: async (data) => {
    const response = await fetch(`${API_BASE}/accounting/project-accounting.php`, {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return parseApiResponse(response);
  },

  updateRecord: async (id, data) => {
    const response = await fetch(`${API_BASE}/accounting/project-accounting.php`, {
      method: 'PUT',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: JSON.stringify({ id, ...data }),
    });
    return parseApiResponse(response);
  },

  deleteRecord: async (id) => {
    const response = await fetch(
      `${API_BASE}/accounting/project-accounting.php?id=${id}`,
      { method: 'DELETE', credentials: 'include', headers: getAuthHeaders() }
    );
    return parseApiResponse(response);
  },

  updateStatus: async (id, status) => {
    const response = await fetch(`${API_BASE}/accounting/project-accounting.php`, {
      method: 'PUT',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: JSON.stringify({ id, status }),
    });
    return parseApiResponse(response);
  },

  uploadFile: async (file, type) => {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await fetch(`${API_BASE}/accounting/upload-file.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      body: formData
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'File upload failed');
    }
    return response.json();
  },

  deleteFile: async (fileName, type) => {
    const response = await fetch(`${API_BASE}/accounting/delete-file.php`, {
      method: 'POST',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: JSON.stringify({ fileName, type })
    });
    return parseApiResponse(response);
  }
};

const projectsAPI = {
  details: async (projectId) => {
    const response = await fetch(
      `${API_BASE}/projects/details.php?id=${projectId}`,
      { method: 'GET', credentials: 'include', headers: getAuthHeaders() }
    );
    return parseApiResponse(response);
  },
};

const parseAttachment = (attachment) => {
  if (!attachment || attachment === 'null' || attachment === '') return [];
  try {
    const parsed = JSON.parse(attachment);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
};

const ProjectAccountingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);

  const [isIncomeModalOpen, setIsIncomeModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    category: '',
  });

  const [vatIncluded, setVatIncluded] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // View Files Dialog
  const [viewingFiles, setViewingFiles] = useState(false);
  const [currentItemFiles, setCurrentItemFiles] = useState([]);
  const [currentViewItem, setCurrentViewItem] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [currentItemTitle, setCurrentItemTitle] = useState('');

  // File Viewer Modal
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [currentViewingFile, setCurrentViewingFile] = useState(null);

  const [accountingData, setAccountingData] = useState({ income: [], expenses: [] });
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.role === 'Admin';
  const isManager = user?.role === 'Manager';
  const isSecretary = user?.role === 'Secretary';
  const canViewAllEntries = isAdmin || isManager;
  const canAdd = isAdmin || isManager || isSecretary;

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const projectResponse = await projectsAPI.details(id);
      if (projectResponse.success) {
        setProject({
          ...projectResponse.data,
          projectName: projectResponse.data.project_name || projectResponse.data.projectName
        });
      }
      const accountingResponse = await accountingAPI.getProjectAccounting(id);
      if (accountingResponse.success) {
        setAccountingData(accountingResponse.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load accounting data', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateFinalAmount = () => {
    const baseAmount = parseFloat(formData.amount) || 0;
    return vatIncluded ? baseAmount * 1.05 : baseAmount;
  };
  const finalAmount = calculateFinalAmount();

  const handleViewFiles = (item) => {
    setLoadingFiles(true);
    setViewingFiles(true);
    setCurrentItemTitle(item.description);
    setCurrentViewItem(item);
    setCurrentItemFiles(parseAttachment(item.attachment));
    setLoadingFiles(false);
  };

  const handleOpenFileViewer = (file) => {
    setCurrentViewingFile(file);
    setFileViewerOpen(true);
  };

  const downloadAttachment = (attachment) => {
    if (!attachment) return;
    if (attachment.fileUrl) {
      const link = document.createElement('a');
      link.href = attachment.fileUrl;
      link.download = attachment.fileName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    if (!attachment.data) {
      toast({ title: "Demo Restriction", description: "File content was not stored." });
      return;
    }
    const link = document.createElement('a');
    link.href = attachment.data;
    link.download = attachment.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteSingleFile = async (fileToDelete, fileIndex) => {
    if (!currentViewItem) return;
    if (!window.confirm(`Delete "${fileToDelete.fileName}"?`)) return;

    const itemType = currentViewItem.type;

    try {
      await accountingAPI.deleteFile(fileToDelete.fileName, itemType);
      const updatedFiles = currentItemFiles.filter((_, i) => i !== fileIndex);
      await accountingAPI.updateRecord(currentViewItem.id, {
        attachment: updatedFiles.length > 0 ? JSON.stringify(updatedFiles) : null
      });
      setCurrentItemFiles(updatedFiles);
      toast({ title: "File Deleted", description: `${fileToDelete.fileName} removed successfully.` });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete file", variant: "destructive" });
    }
  };

  const handleOpenModal = (type, item = null) => {
    setAttachedFiles([]);
    setExistingFiles([]);
    setVatIncluded(false);
    setIsUploading(false);

    if (item) {
      setEditingItem(item);
      setFormData({
        description: item.description,
        amount: item.amount_before_vat || item.amount,
        date: item.date.split('T')[0],
        category: item.category || '',
      });
      if (item.vat_included == 1) setVatIncluded(true);
      setExistingFiles(parseAttachment(item.attachment));
    } else {
      setEditingItem(null);
      setFormData({
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        category: '',
      });
    }

    if (type === 'income') setIsIncomeModalOpen(true);
    else setIsExpenseModalOpen(true);
  };

  const handleFileSelect = (file) => {
    setAttachedFiles(prev => [...prev, file]);
    toast({ title: "File Added", description: `${file.name} ready for upload.` });
  };

  const handleRemoveAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (index) => {
    setExistingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (type) => {
    if (!formData.description || !formData.amount || !formData.date) {
      toast({ title: "Validation Error", description: "Please fill all required fields", variant: "destructive" });
      return;
    }
    if (parseFloat(formData.amount) <= 0) {
      toast({ title: "Validation Error", description: "Amount must be a positive number", variant: "destructive" });
      return;
    }
    if (type === 'expense' && !formData.category) {
      toast({ title: "Validation Error", description: "Please select a category", variant: "destructive" });
      return;
    }

    const baseAmount = parseFloat(formData.amount);
    const finalCalculatedAmount = vatIncluded ? baseAmount * 1.05 : baseAmount;

    let allFiles = [...existingFiles];

    if (attachedFiles.length > 0) {
      setIsUploading(true);
      try {
        for (const file of attachedFiles) {
          const uploadResult = await accountingAPI.uploadFile(file, type);
          if (!uploadResult.success) throw new Error(uploadResult.error || 'File upload failed');
          allFiles.push({
            fileName: uploadResult.fileName,
            fileSize: (uploadResult.fileSize / 1024 / 1024).toFixed(2) + ' MB',
            fileType: file.type,
            fileUrl: uploadResult.fileUrl,
            folder: uploadResult.folder,
            uploadDate: new Date().toISOString()
          });
        }
      } catch (error) {
        toast({ title: "Error", description: error.message || 'Failed to upload file.', variant: "destructive" });
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const itemData = {
      description: formData.description,
      amount: finalCalculatedAmount,
      amount_before_vat: vatIncluded ? baseAmount : null,
      vat_included: vatIncluded ? 1 : 0,
      date: new Date(formData.date).toISOString(),
      category: type === 'expense' ? formData.category : undefined,
      addedBy: editingItem ? editingItem.addedBy : user.name,
      addedById: editingItem ? (editingItem.addedById || user.id) : user.id,
      status: editingItem ? editingItem.status : 'Pending',
      approvedBy: editingItem?.approvedBy || null,
      approvedAt: editingItem?.approvedAt || null,
      attachment: allFiles.length > 0 ? JSON.stringify(allFiles) : null
    };

    try {
      if (editingItem) {
        await accountingAPI.updateRecord(editingItem.id, itemData);
        toast({ title: "Updated Successfully", description: `${type === 'income' ? 'Income' : 'Expense'} updated.` });
      } else {
        await accountingAPI.createRecord({ projectId: id, type, ...itemData });
        toast({ title: "Added Successfully", description: `New ${type} entry added. Waiting for approval.` });
        createNotification('ADMIN', 'INFO', `${user.name} added a new ${type} entry of ${formatCurrency(itemData.amount)} for approval.`);
      }
      setIsIncomeModalOpen(false);
      setIsExpenseModalOpen(false);
      loadData();
    } catch (error) {
      toast({ title: "Error", description: error.message || 'Failed to save record', variant: "destructive" });
    }
  };

  const handleDelete = async (recordId, type) => {
    if (!isAdmin) return;
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const itemList = type === 'income' ? incomeList : expenseList;
      const item = itemList.find(i => i.id === recordId);

      if (item && item.attachment) {
        const files = parseAttachment(item.attachment);
        for (const f of files) {
          try {
            await accountingAPI.deleteFile(f.fileName, type);
          } catch (e) {
            console.warn('Failed to delete file:', f.fileName, e);
          }
        }
      }

      await accountingAPI.deleteRecord(recordId);
      toast({ title: "Deleted", description: "Item removed successfully." });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete record", variant: "destructive" });
    }
  };

  const handleStatusChange = async (id, type, newStatus) => {
    if (!isAdmin) return;
    try {
      await accountingAPI.updateStatus(id, newStatus);
      const msg = newStatus === 'Approved' ? "Entry Approved" : "Entry Rejected";
      toast({ title: msg, description: `Status updated to ${newStatus}` });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  // Export Income + Expenses في شيتين منفصلين
  const handleExportAllToExcel = () => {
    if (incomeList.length === 0 && expenseList.length === 0) {
      toast({ 
        title: "No Data", 
        description: "No accounting entries to export.", 
        variant: "destructive" 
      });
      return;
    }

    const wb = XLSX.utils.book_new();

    if (incomeList.length > 0) {
      const incomeData = incomeList.map(item => ({
        Description: item.description,
        'Amount (AED)': parseFloat(item.amount).toFixed(2),
        'VAT Included': item.vat_included == 1 ? 'Yes' : 'No',
        Date: new Date(item.date).toLocaleDateString(),
        Status: item.status,
        'Added By': item.addedBy || item.added_by,
        'Approved By': item.approvedBy || '-',
        'Files Attached': parseAttachment(item.attachment).length
      }));
      const incomeWs = XLSX.utils.json_to_sheet(incomeData);
      XLSX.utils.book_append_sheet(wb, incomeWs, 'Income');
    }

    if (expenseList.length > 0) {
      const expenseData = expenseList.map(item => ({
        Description: item.description,
        Category: item.category,
        'Amount (AED)': parseFloat(item.amount).toFixed(2),
        'VAT Included': item.vat_included == 1 ? 'Yes' : 'No',
        Date: new Date(item.date).toLocaleDateString(),
        Status: item.status,
        'Added By': item.addedBy || item.added_by,
        'Approved By': item.approvedBy || '-',
        'Files Attached': parseAttachment(item.attachment).length
      }));
      const expenseWs = XLSX.utils.json_to_sheet(expenseData);
      XLSX.utils.book_append_sheet(wb, expenseWs, 'Expenses');
    }

    const fileName = `${project?.projectName || 'Project'}_Accounting_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);

    toast({ 
      title: "Excel Exported", 
      description: `Accounting data exported successfully.` 
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!project) return null;

  const incomeList = canViewAllEntries
    ? accountingData.income
    : accountingData.income.filter(i => i.addedBy === user.name || i.added_by === user.name);

  const expenseList = canViewAllEntries
    ? accountingData.expenses
    : accountingData.expenses.filter(e => e.addedBy === user.name || e.added_by === user.name);

  incomeList.sort((a, b) => new Date(b.date) - new Date(a.date));
  expenseList.sort((a, b) => new Date(b.date) - new Date(a.date));

  const calcStats = (list) => ({
    approved: list.filter(i => i.status === 'Approved').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0),
    pending: list.filter(i => i.status === 'Pending').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0),
    rejected: list.filter(i => i.status === 'Rejected').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0),
  });

  const incomeStats = calcStats(incomeList);
  const expenseStats = calcStats(expenseList);
  const totalApprovedIncome = incomeStats.approved;
  const totalApprovedExpense = expenseStats.approved;
  const netProfit = totalApprovedIncome - totalApprovedExpense;
  const isProfit = netProfit >= 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Approved': return <Badge className="bg-emerald-500 hover:bg-emerald-600 border-none text-white">Approved</Badge>;
      case 'Rejected': return <Badge className="bg-red-500 hover:bg-red-600 border-none text-white">Rejected</Badge>;
      default: return <Badge className="bg-amber-500 hover:bg-amber-600 border-none text-white">Pending</Badge>;
    }
  };

  const renderTable = (items, type) => (
    <div className="rounded-md border border-stone-200 overflow-hidden bg-white shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between bg-slate-50 p-3 border-b border-stone-200">
        <h2 className={cn("text-base font-bold flex items-center gap-2", type === 'income' ? "text-green-700" : "text-red-700")}>
          {type === 'income' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {type === 'income' ? 'Income Entries' : 'Expense Entries'}
        </h2>
        <div className="flex items-center gap-2">
          {canAdd && (
            <Button
              size="sm"
              onClick={() => handleOpenModal(type)}
              className={cn("text-xs h-7 px-2", type === 'income' ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700")}
            >
              <Plus className="w-3 h-3 mr-1" /> Add {type === 'income' ? 'Income' : 'Expense'}
            </Button>
          )}
        </div>
      </div>
      <div className="overflow-auto flex-1">
        <Table>
          <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-sm">
            <TableRow className="hover:bg-slate-50 border-stone-200">
              <TableHead className="w-[30%] text-xs font-bold text-stone-600 uppercase tracking-wider py-2 h-9">Description</TableHead>
              {type === 'expense' && <TableHead className="text-xs font-bold text-stone-600 uppercase tracking-wider py-2 h-9">Category</TableHead>}
              <TableHead className="text-xs font-bold text-stone-600 uppercase tracking-wider py-2 h-9">Amount</TableHead>
              <TableHead className="text-xs font-bold text-stone-600 uppercase tracking-wider py-2 h-9 w-[40px] text-center">
                <Paperclip className="w-3.5 h-3.5 mx-auto" />
              </TableHead>
              <TableHead className="text-xs font-bold text-stone-600 uppercase tracking-wider py-2 h-9">Date</TableHead>
              <TableHead className="text-xs font-bold text-stone-600 uppercase tracking-wider py-2 h-9">Status</TableHead>
              <TableHead className="text-right text-xs font-bold text-stone-600 uppercase tracking-wider py-2 h-9">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={type === 'expense' ? 7 : 6} className="h-32 text-center text-stone-400 text-sm">
                  No {type} entries visible.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const isOwner = item.addedBy === user.name || item.added_by === user.name;
                const canEditThis = isOwner || isAdmin;
                const hasAttachment = item.attachment && item.attachment !== 'null' && item.attachment !== '';
                const filesCount = parseAttachment(item.attachment).length;

                return (
                  <TableRow key={item.id} className="group hover:bg-slate-50/80 transition-colors border-stone-100">
                    <TableCell className="font-medium py-2 text-xs">
                      <div className="truncate max-w-[150px] lg:max-w-[200px]" title={item.description}>
                        {item.description}
                      </div>
                      {item.vat_included == 1 && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium ml-2">
                          +5% VAT
                        </span>
                      )}
                      <div className="text-[10px] text-stone-400 mt-0.5 flex items-center gap-1">
                        <span className={cn("w-1.5 h-1.5 rounded-full", isOwner ? "bg-blue-400" : "bg-stone-300")} />
                        {isOwner ? "Me" : (item.addedBy || item.added_by || 'System')}
                      </div>
                    </TableCell>
                    {type === 'expense' && (
                      <TableCell className="py-2">
                        <Badge variant="outline" className="text-[10px] h-5 font-normal text-stone-500 bg-stone-50 border-stone-200">
                          {item.category}
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell className="font-semibold text-navy-steal py-2 text-xs">
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      {hasAttachment ? (
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 rounded-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 relative"
                                onClick={() => handleViewFiles(item)}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                {filesCount > 1 && (
                                  <span className="absolute -top-1 -right-1 bg-safety-orange text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
                                    {filesCount}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs bg-navy-steal text-white border-none z-50">
                              <p>{filesCount} file{filesCount > 1 ? 's' : ''} attached</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-stone-300 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-stone-500 py-2 text-xs whitespace-nowrap">
                      {new Date(item.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="py-2 scale-90 origin-left">
                      {getStatusBadge(item.status)}
                    </TableCell>
                    <TableCell className="text-right py-2">
                      <div className="flex justify-end items-center gap-1">
                        {isAdmin && item.status === 'Pending' && (
                          <>
                            <Button size="icon" variant="ghost"
                              className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full"
                              onClick={() => handleStatusChange(item.id, type, 'Approved')} title="Approve">
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost"
                              className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                              onClick={() => handleStatusChange(item.id, type, 'Rejected')} title="Reject">
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {canEditThis && (
                          <Button size="icon" variant="ghost"
                            className="h-6 w-6 text-stone-500 hover:text-navy-steal hover:bg-slate-100 rounded-full"
                            onClick={() => handleOpenModal(type, item)} title="Edit">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="icon" variant="ghost"
                            className="h-6 w-6 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                            onClick={() => handleDelete(item.id, type)} title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderModalContent = (type) => (
    <>
      <div className="grid gap-4 py-4">
        <div className="grid gap-2">
          <Label htmlFor={`${type}-desc`}>Description <span className="text-red-500">*</span></Label>
          <Input
            id={`${type}-desc`}
            required
            placeholder={type === 'income' ? "e.g. Initial Deposit" : "e.g. Materials purchase"}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        {type === 'expense' && (
          <div className="grid gap-2">
            <Label htmlFor="expense-category">Category <span className="text-red-500">*</span></Label>
            <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
              <SelectTrigger id="expense-category">
                <SelectValue placeholder="Select Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Materials">Materials</SelectItem>
                <SelectItem value="Labor">Labor</SelectItem>
                <SelectItem value="Subcontractor">Subcontractor</SelectItem>
                <SelectItem value="Equipment">Equipment</SelectItem>
                <SelectItem value="Transport">Transport</SelectItem>
                <SelectItem value="Office">Office & Admin</SelectItem>
                <SelectItem value="Utilities">Utilities</SelectItem>
                <SelectItem value="Vendor">Vendor</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor={`${type}-amount`}>Amount (AED) <span className="text-red-500">*</span></Label>
            <Input
              id={`${type}-amount`}
              type="number" min="0" step="0.01" placeholder="0.00"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${type}-date`}>Date <span className="text-red-500">*</span></Label>
            <Input
              id={`${type}-date`}
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
          </div>
        </div>

        <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border">
          <input
            type="checkbox" id="vatIncluded" checked={vatIncluded}
            onChange={(e) => setVatIncluded(e.target.checked)}
            className="w-4 h-4 text-safety-orange bg-gray-100 border-gray-300 rounded focus:ring-safety-orange focus:ring-2"
          />
          <label htmlFor="vatIncluded" className="text-sm font-medium text-stone-700 cursor-pointer">Add 5% VAT</label>
        </div>

        {formData.amount && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center text-sm">
              <span className="text-stone-600">Base Amount:</span>
              <span className="font-mono font-semibold">{formatCurrency(parseFloat(formData.amount) || 0)}</span>
            </div>
            {vatIncluded && (
              <>
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="text-stone-600">VAT (5%):</span>
                  <span className="font-mono font-semibold text-green-600">
                    +{formatCurrency((parseFloat(formData.amount) || 0) * 0.05)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2 pt-2 border-t border-blue-300">
                  <span className="font-bold text-navy-steal">Total Amount:</span>
                  <span className="font-mono font-bold text-navy-steal text-lg">{formatCurrency(finalAmount)}</span>
                </div>
              </>
            )}
          </div>
        )}

        {editingItem && existingFiles.length > 0 && (
          <div className="grid gap-2 pt-2 border-t border-stone-100">
            <Label>Existing Files ({existingFiles.length})</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {existingFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between bg-blue-50 border border-blue-200 p-2 rounded-md text-sm">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="truncate max-w-[200px]">{file.fileName}</span>
                    <span className="text-xs text-stone-400 shrink-0">({file.fileSize})</span>
                  </div>
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                    onClick={() => handleRemoveExistingFile(idx)}
                    title="Remove from list (won't delete from server until saved)"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-2 pt-2 border-t border-stone-100">
          <Label className="flex items-center gap-2">
            <Paperclip className="w-3.5 h-3.5" /> Add More Files (Optional - Max 250MB each)
          </Label>
          <div className="border border-stone-200 rounded-md p-1">
            <FileUpload
              onFileSelect={handleFileSelect}
              onUploadStatusChange={setIsUploading}
              label="Click to attach files"
              maxSizeMB={250}
              multiple={true}
            />
          </div>

          {attachedFiles.length > 0 && (
            <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
              {attachedFiles.map((f, i) => (
                <div key={i} className="flex justify-between items-center text-xs bg-green-50 border border-green-200 p-2 rounded border">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="w-4 h-4 text-green-600 shrink-0" />
                    <span className="truncate">{f.name}</span>
                    <span className="text-stone-400 shrink-0">({(f.size / (1024 * 1024)).toFixed(2)} MB)</span>
                  </div>
                  <button type="button" onClick={() => handleRemoveAttachedFile(i)}
                    className="text-red-500 hover:text-red-700 shrink-0 ml-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <Helmet><title>Accounting - {project.projectName}</title></Helmet>

      {/* View Files Dialog */}
      <Dialog open={viewingFiles} onOpenChange={setViewingFiles}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Attached Files</DialogTitle>
            <DialogDescription>{currentItemTitle}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {loadingFiles ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-safety-orange"></div>
              </div>
            ) : currentItemFiles.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <FileText className="w-12 h-12 mx-auto mb-2 text-stone-300" />
                <p>No files attached</p>
              </div>
            ) : (
              currentItemFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{file.fileName}</p>
                      <p className="text-xs text-stone-500">{file.fileSize}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost"
                      onClick={() => handleOpenFileViewer(file)}
                      title="View File">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={() => downloadAttachment(file)}
                      disabled={file.isSimulated && !file.fileUrl}
                      title="Download">
                      <Download className="w-4 h-4" />
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost"
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteSingleFile(file, idx)}
                        title="Delete this file">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog open={fileViewerOpen} onOpenChange={setFileViewerOpen}>
        <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {currentViewingFile?.fileName}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-4 text-xs">
              <span>{currentViewingFile?.fileSize}</span>
              <span className="text-stone-400">|</span>
              <a 
                href={currentViewingFile?.fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Open in New Tab <ExternalLink className="w-3 h-3" />
              </a>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto p-4 bg-slate-50">
            {currentViewingFile?.fileUrl && (
              currentViewingFile.fileType?.includes('image') ? (
                <img 
                  src={currentViewingFile.fileUrl} 
                  alt={currentViewingFile.fileName}
                  className="max-w-full h-auto mx-auto rounded shadow-lg"
                />
              ) : currentViewingFile.fileType?.includes('pdf') ? (
                <iframe 
                  src={currentViewingFile.fileUrl} 
                  className="w-full h-[70vh] border-0 rounded shadow-lg"
                  title={currentViewingFile.fileName}
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                  <p className="text-stone-500 mb-4">Preview not available for this file type</p>
                  <Button onClick={() => downloadAttachment(currentViewingFile)}>
                    <Download className="w-4 h-4 mr-2" /> Download File
                  </Button>
                </div>
              )
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setFileViewerOpen(false)}>Close</Button>
            <Button onClick={() => downloadAttachment(currentViewingFile)}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-6 animate-in fade-in duration-500 h-[calc(100vh-100px)] flex flex-col">
        {/* Header with project name + Export */}
        <div className="bg-gradient-to-r from-navy-steal to-slate-700 text-white p-4 rounded-lg shadow-lg shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-300 mb-1">Project Accounting</p>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-safety-orange" />
                {project.projectName}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleExportAllToExcel}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Export to Excel
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate(`/projects/${id}`)} 
                className="bg-white text-navy-steal hover:bg-slate-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Project
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 shrink-0">
          <Card className="bg-white border-l-4 border-l-emerald-500 shadow-sm">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Total Income</p>
                  <h3 className="text-xl font-bold text-emerald-700 mt-1">{formatCurrency(totalApprovedIncome)}</h3>
                  <p className="text-[10px] text-stone-400 mt-1">All approved income</p>
                </div>
                <div className="p-2 bg-emerald-50 rounded-full">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-l-4 border-l-red-500 shadow-sm">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Total Expenses</p>
                  <h3 className="text-xl font-bold text-red-700 mt-1">{formatCurrency(totalApprovedExpense)}</h3>
                  <p className="text-[10px] text-stone-400 mt-1">All approved expenses</p>
                </div>
                <div className="p-2 bg-red-50 rounded-full">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {canViewAllEntries ? (
            <Card className={cn("bg-white border-l-4 shadow-sm", isProfit ? "border-l-navy-steal" : "border-l-orange-500")}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Net Profit</p>
                    <h3 className={cn("text-xl font-bold mt-1", isProfit ? "text-navy-steal" : "text-orange-600")}>
                      {formatCurrency(netProfit)}
                    </h3>
                    <p className="text-[10px] text-stone-400 mt-1">Income - Expenses (Approved)</p>
                  </div>
                  <div className={cn("p-2 rounded-full", isProfit ? "bg-slate-100" : "bg-orange-50")}>
                    <DollarSign className={cn("w-4 h-4", isProfit ? "text-navy-steal" : "text-orange-600")} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-l-4 border-l-red-500 shadow-sm">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">My Rejected</p>
                    <h3 className="text-xl font-bold text-red-600 mt-1">
                      {formatCurrency(incomeStats.rejected + expenseStats.rejected)}
                    </h3>
                    <p className="text-[10px] text-stone-400 mt-1">Needs correction</p>
                  </div>
                  <div className="p-2 bg-red-50 rounded-full">
                    <XCircle className="w-4 h-4 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-full min-h-[400px]">{renderTable(incomeList, 'income')}</div>
          <div className="h-full min-h-[400px]">{renderTable(expenseList, 'expense')}</div>
        </div>
      </div>

      {/* Income Modal */}
      <Dialog open={isIncomeModalOpen} onOpenChange={setIsIncomeModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>{editingItem ? 'Edit Income' : 'Add New Income'}</DialogTitle>
            <DialogDescription>
              {isSecretary ? "Submit an income entry for admin approval." : "Record a new income payment or deposit."}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6">{renderModalContent('income')}</div>
          <DialogFooter className="px-6 py-4 border-t bg-slate-50 shrink-0 mt-auto">
            <Button variant="outline" onClick={() => setIsIncomeModalOpen(false)}>Cancel</Button>
            <Button onClick={() => handleSave('income')} className="bg-green-600 hover:bg-green-700" disabled={isUploading}>
              {isUploading ? 'Uploading...' : editingItem ? 'Save Changes' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Modal */}
      <Dialog open={isExpenseModalOpen} onOpenChange={setIsExpenseModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>{editingItem ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            <DialogDescription>
              {isSecretary ? "Submit an expense request for admin approval." : "Record a project-related expense."}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6">{renderModalContent('expense')}</div>
          <DialogFooter className="px-6 py-4 border-t bg-slate-50 shrink-0 mt-auto">
            <Button variant="outline" onClick={() => setIsExpenseModalOpen(false)}>Cancel</Button>
            <Button onClick={() => handleSave('expense')} className="bg-red-600 hover:bg-red-700" disabled={isUploading}>
              {isUploading ? 'Uploading...' : editingItem ? 'Save Changes' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectAccountingPage;
